import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Share2, ExternalLink, MessageCircle, Link2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog.js";
import { Button } from "./ui/button.js";

export interface CompartirDocumentoData {
  id: number;
  tipoDte: number;
  folio: number | null;
  rutEmisor: string;
  receptorRut: string | null;
  receptorNombre: string | null;
  montoTotal: number;
  fechaEmision: string;
  shareHash?: string;
  notaCredito?: { id: number; folio: number; shareHash?: string } | null;
}

interface CompartirModalProps {
  open: boolean;
  onClose: () => void;
  doc: CompartirDocumentoData | null;
}

function getTipoNombre(tipoDte: number): string {
  switch (tipoDte) {
    case 69: return "Boleta de Honorarios";
    case 39: return "Boleta Electrónica Afecta";
    case 41: return "Boleta Electrónica Exenta";
    case 33: return "Factura Electrónica";
    case 34: return "Factura Exenta";
    case 61: return "Nota de Crédito";
    default: return `Documento DTE ${tipoDte}`;
  }
}

export default function CompartirModal({ open, onClose, doc }: CompartirModalProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedNcLink, setCopiedNcLink] = useState(false);
  const [copiedText, setCopiedText] = useState(false);

  if (!doc) return null;

  const tipo = getTipoNombre(doc.tipoDte);
  const folio = doc.folio ?? "—";
  const shareUrl = doc.shareHash
    ? `${window.location.origin}/api/publico/documentos/${doc.id}/descargar?h=${doc.shareHash}`
    : `${window.location.origin}/api/documentos/${doc.id}/descargar`;

  const ncShareUrl = doc.notaCredito
    ? doc.notaCredito.shareHash
      ? `${window.location.origin}/api/publico/documentos/${doc.notaCredito.id}/descargar?h=${doc.notaCredito.shareHash}`
      : `${window.location.origin}/api/documentos/${doc.notaCredito.id}/descargar`
    : null;

  const receptor = doc.receptorNombre || (doc.tipoDte === 39 || doc.tipoDte === 41 ? "Consumidor Final" : "Cliente");
  const monto = `$${doc.montoTotal.toLocaleString("es-CL")}`;
  const fecha = new Date(doc.fechaEmision).toLocaleDateString("es-CL");

  const textoResumen = doc.notaCredito
    ? `*${tipo} #${folio} (ANULADA)*\n` +
      `⚠️ Documento anulado mediante Nota de Crédito #${doc.notaCredito.folio}\n` +
      `👤 Receptor: ${receptor}\n` +
      `💰 Monto: ${monto}\n` +
      `📅 Fecha: ${fecha}\n\n` +
      `📄 ${tipo} #${folio}:\n${shareUrl}\n\n` +
      `📑 Nota de Crédito #${doc.notaCredito.folio}:\n${ncShareUrl}`
    : `*${tipo} #${folio}*\n` +
      `👤 Receptor: ${receptor}\n` +
      `💰 Monto: ${monto}\n` +
      `📅 Fecha: ${fecha}\n\n` +
      `Descargar documento oficial:\n${shareUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success("Enlace de descarga copiado al portapapeles");
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const handleCopyNcLink = async () => {
    if (!ncShareUrl) return;
    try {
      await navigator.clipboard.writeText(ncShareUrl);
      setCopiedNcLink(true);
      toast.success("Enlace de la Nota de Crédito copiado");
      setTimeout(() => setCopiedNcLink(false), 2000);
    } catch {
      toast.error("No se pudo copiar el enlace de la Nota de Crédito");
    }
  };

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(textoResumen);
      setCopiedText(true);
      toast.success("Resumen copiado al portapapeles");
      setTimeout(() => setCopiedText(false), 2000);
    } catch {
      toast.error("No se pudo copiar el texto");
    }
  };

  const handleWhatsApp = () => {
    const msg = doc.notaCredito
      ? `Hola${doc.receptorNombre ? ` ${doc.receptorNombre}` : ""}, te comparto tu ${tipo} Folio #${folio} y su Nota de Crédito de anulación #${doc.notaCredito.folio}:\n\n` +
        `📄 Documento original #${folio}:\n${shareUrl}\n\n` +
        `📑 Nota de Crédito #${doc.notaCredito.folio}:\n${ncShareUrl}`
      : `Hola${doc.receptorNombre ? ` ${doc.receptorNombre}` : ""}, te comparto tu ${tipo} Folio #${folio} por ${monto}:\n\n${shareUrl}`;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: doc.notaCredito ? `${tipo} #${folio} (Anulada con NC #${doc.notaCredito.folio})` : `${tipo} #${folio}`,
          text: `Comprobante ${tipo} #${folio} para ${receptor}`,
          url: shareUrl,
        });
      } catch (err) {
        // Usuario canceló compartir o no soportado
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
              <Share2 className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                Compartir {tipo} #{folio}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Envía o comparte el comprobante oficial con tu cliente
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Banner de Anulación con NC si aplica */}
        {doc.notaCredito && (
          <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-lg p-2.5 text-xs text-rose-800 dark:text-rose-200 flex items-center justify-between">
            <span className="font-medium">Documento Anulado</span>
            <span className="font-semibold text-rose-700 dark:text-rose-300">Nota de Crédito #{doc.notaCredito.folio}</span>
          </div>
        )}

        {/* Resumen Card */}
        <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-lg p-3 space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Receptor:</span>
            <span className="font-medium text-foreground">{receptor}</span>
          </div>
          {doc.receptorRut && doc.receptorRut !== "66666666-6" && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">RUT:</span>
              <span className="font-mono text-foreground">{doc.receptorRut}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total:</span>
            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{monto}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Fecha:</span>
            <span className="text-foreground">{fecha}</span>
          </div>
        </div>

        {/* Acciones de Compartir */}
        <div className="space-y-2 pt-1">
          {/* Opción WhatsApp */}
          <Button
            type="button"
            onClick={handleWhatsApp}
            className="w-full justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs"
          >
            <MessageCircle className="h-4 w-4" />
            <span>{doc.notaCredito ? "Enviar Boleta y NC por WhatsApp" : "Enviar por WhatsApp"}</span>
            <ExternalLink className="h-3 w-3 opacity-70 ml-auto" />
          </Button>

          {/* Opción Copiar Enlace Directo Boleta */}
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyLink}
            className="w-full justify-between gap-2 border-slate-200 hover:bg-slate-50 font-normal"
          >
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-sky-600" />
              <span>{doc.notaCredito ? `Copiar enlace ${tipo}` : "Copiar enlace de descarga directa"}</span>
            </div>
            {copiedLink ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
          </Button>

          {/* Opción Copiar Enlace Directo NC si aplica */}
          {ncShareUrl && doc.notaCredito && (
            <Button
              type="button"
              variant="outline"
              onClick={handleCopyNcLink}
              className="w-full justify-between gap-2 border-rose-200 bg-rose-50/40 hover:bg-rose-100/60 text-rose-900 dark:text-rose-200 dark:border-rose-900/60 font-normal"
            >
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-rose-600" />
                <span>Copiar enlace Nota de Crédito (#{doc.notaCredito.folio})</span>
              </div>
              {copiedNcLink ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
            </Button>
          )}

          {/* Opción Copiar Resumen de Texto */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCopyText}
            className="w-full justify-between gap-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <span>{doc.notaCredito ? "Copiar texto con ambos enlaces" : "Copiar texto del comprobante"}</span>
            {copiedText ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>

          {/* Compartir Nativo (si el navegador lo soporta) */}
          {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNativeShare}
              className="w-full gap-2 text-xs"
            >
              <Share2 className="h-3.5 w-3.5" />
              <span>Más opciones (apps instaladas)</span>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
